import React, { useState, useEffect, useRef, use } from 'react';
import { LuSearch, LuUser, LuMenu, LuSettings, LuLogOut } from 'react-icons/lu';
import { useAuth } from '../../providers/auth-provider';
import { Link } from 'react-router-dom';

interface NavbarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  setIsMobileOpen: (value: boolean) => void;
}

const Navbar = ({ isCollapsed, isMobileOpen, setIsMobileOpen }: NavbarProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { logout, user } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 right-0 z-10 ${isCollapsed ? 'lg:left-20' : 'lg:left-72'} left-0 bg-white border-b border-gray-200 transition-all duration-300`}>
      <div className="px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          >
            <LuMenu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="relative hidden sm:block">
            <LuSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-primary-600 w-72"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 border-l pl-4 hover:bg-gray-50 rounded-lg p-1 transition-colors"
            >
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-sm font-medium">{user?.details.first_name}</span>
                <span className="text-xs text-gray-500 capitalize">{user?.role}</span>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <LuUser className="w-5 h-5 text-gray-600" />
              </div>
            </button>

            {/* LuUser Dropdown LuMenu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-30">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">{user?.details.first_name + ' ' + user?.details.last_name}</p>
                  <p className="text-sm text-gray-500">{user?.details.email}</p>
                </div>

                <div className="border-t border-gray-100">
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 p-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <LuUser className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                </div>

                <div className="border-t border-gray-100">
                  <button
                    onClick={logout}
                    className="flex items-center gap-3 p-4 text-sm text-red-600 hover:bg-red-50 transition-colors w-full"
                  >
                    <LuLogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;